import { supabase } from '@/lib/supabase';
import { settingsService } from './settingsService';

export interface Branch {
  id: string;
  company_id: string;
  name: string;
  code?: string; // Human-readable code: BR-001, BR-002, etc.
  branch_code?: string; // Alias for code (for UI consistency)
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  is_active: boolean;
  fiscal_year_start?: string | null; // YYYY-MM-DD
  fiscal_year_end?: string | null; // YYYY-MM-DD
  default_cash_account_id?: string | null;
  default_bank_account_id?: string | null;
  default_pos_drawer_account_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** Branch access mode: AUTO = single-branch company (auto-assign); RESTRICTED = multi-branch (user must be assigned). */
export type BranchAccessMode = 'AUTO' | 'RESTRICTED';

/** In-memory cache for branch list by company (avoids repeated DB calls). Call clearBranchCache() on sign-out. */
const branchListCache = new Map<string, { data: Branch[]; ts: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const branchService = {
  /** Clear in-memory branch cache (call on sign-out). */
  clearBranchCache(): void {
    branchListCache.clear();
  },

  /** Get all branches for a company (cached). Use for BranchSelector and filter bars. */
  async getBranchesCached(companyId: string): Promise<Branch[]> {
    const entry = branchListCache.get(companyId);
    if (entry && Date.now() - entry.ts < CACHE_TTL_MS) return entry.data;
    const data = await this.getAllBranches(companyId);
    branchListCache.set(companyId, { data: data as Branch[], ts: Date.now() });
    return data as Branch[];
  },

  /** Get count of active branches for a company. Used for Branch Access Mode (1 = AUTO, >1 = RESTRICTED). */
  async getCompanyBranchCount(companyId: string): Promise<number> {
    const { count, error } = await supabase
      .from('branches')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .or('is_active.is.null,is_active.eq.true');
    if (error) throw error;
    return count ?? 0;
  },

  // Get all branches for a company (active only — selectors, filters)
  async getAllBranches(companyId: string) {
    const { data, error } = await supabase
      .from('branches')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('name');

    if (error) throw error;
    return data;
  },

  /** Settings / management: all branches (active + inactive). */
  async getBranchesForManagement(companyId: string): Promise<Branch[]> {
    const { data, error } = await supabase
      .from('branches')
      .select('*')
      .eq('company_id', companyId)
      .order('name');

    if (error) throw error;
    return (data || []) as Branch[];
  },

  // Get single branch
  async getBranch(id: string) {
    const { data, error } = await supabase
      .from('branches')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Generate next branch code
  async generateBranchCode(companyId: string): Promise<string> {
    try {
      // Try to use document sequence
      return await settingsService.getNextDocumentNumber(companyId, undefined, 'branch');
    } catch (error) {
      // Fallback: Get max code and increment
      const { data: branches } = await supabase
        .from('branches')
        .select('code')
        .eq('company_id', companyId)
        .not('code', 'is', null)
        .order('code', { ascending: false })
        .limit(1);

      if (branches && branches.length > 0 && branches[0].code) {
        const lastCode = branches[0].code;
        const match = lastCode.match(/BR-(\d+)/);
        if (match) {
          const nextNum = parseInt(match[1]) + 1;
          return `BR-${String(nextNum).padStart(3, '0')}`;
        }
      }
      
      // Default: start from BR-001
      return 'BR-001';
    }
  },

  // Create branch
  async createBranch(branch: Partial<Branch>) {
    console.log('[BRANCH SERVICE] Creating branch with payload:', JSON.stringify(branch, null, 2));
    
    try {
      // Auto-generate code if not provided
      if (!branch.code && branch.company_id) {
        branch.code = await this.generateBranchCode(branch.company_id);
        console.log('[BRANCH SERVICE] Auto-generated branch code:', branch.code);
      }

      const { data, error } = await supabase
        .from('branches')
        .insert(branch)
        .select()
        .single();

      if (error) {
        console.error('[BRANCH SERVICE] Error creating branch:', error);
        throw error;
      }
      
      // CRITICAL: Create default accounts for new branch (Cash, Bank, Other)
      if (data && data.company_id) {
        try {
          const { defaultAccountsService } = await import('@/app/services/defaultAccountsService');
          await defaultAccountsService.ensureDefaultAccounts(data.company_id);
          console.log('[BRANCH SERVICE] ✅ Default accounts ensured for branch:', data.id);
        } catch (accountError) {
          console.warn('[BRANCH SERVICE] Warning: Could not create default accounts:', accountError);
          // Don't block branch creation if account creation fails
        }
      }

      // Ensure company has one Walk-in Customer (company-level only; no branch)
      if (data && data.company_id) {
        try {
          const { contactService } = await import('@/app/services/contactService');
          await contactService.createDefaultWalkingCustomer(data.company_id);
          console.log('[BRANCH SERVICE] ✅ Walk-in customer ensured for company:', data.company_id);
        } catch (customerError) {
          console.warn('[BRANCH SERVICE] Warning: Could not ensure walk-in customer:', customerError);
        }
      }
      
      console.log('[BRANCH SERVICE] Branch created successfully:', data);
      if (branch.company_id) {
        branchListCache.delete(branch.company_id);
      }
      return data;
    } catch (error: any) {
      console.error('[BRANCH SERVICE] Exception creating branch:', error);
      throw error;
    }
  },

  // Update branch
  async updateBranch(id: string, updates: Partial<Branch>) {
    console.log('[BRANCH SERVICE] Updating branch:', id, 'with payload:', JSON.stringify(updates, null, 2));
    
    try {
      const { data, error } = await supabase
        .from('branches')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('[BRANCH SERVICE] Error updating branch:', error);
        throw error;
      }
      
      console.log('[BRANCH SERVICE] Branch updated successfully:', data);
      if (data?.company_id) {
        branchListCache.delete(data.company_id);
      }
      return data;
    } catch (error: any) {
      console.error('[BRANCH SERVICE] Exception updating branch:', error);
      throw error;
    }
  },

  // Delete branch (soft delete)
  async deleteBranch(id: string) {
    const { data: row, error: fetchErr } = await supabase
      .from('branches')
      .select('company_id')
      .eq('id', id)
      .maybeSingle();
    if (fetchErr) throw fetchErr;

    const { error } = await supabase
      .from('branches')
      .update({ is_active: false })
      .eq('id', id);

    if (error) throw error;
    const companyId = (row as { company_id?: string } | null)?.company_id;
    if (companyId) branchListCache.delete(companyId);
  },
};
